import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import * as utils from '../../common/Utilities';
import { Box, Typography, CircularProgress } from '@mui/material';
import './Index.css';
import DisplayMedia from '../../components/DisplayMedia';
import { API, graphqlOperation } from 'aws-amplify';
import {
  blankIfValue,
  defaultDateFormat,
  graphqlDateFormat,
  LOG,
} from '../../common/Utilities';
import { createReward } from '../../../graphql/mutations';
import * as mutations from '../../../graphql/mutations';
import gql from 'graphql-tag';
import {
  DateContainer,
  InputBodyContainer,
  InputTitleContainer,
  InputTitleIcon,
  InputTitleText,
  NewRewardTitle,
  ViewInputsContainer,
} from './Rewards.styles';
import UploadMediaNewReward from './UploadMediaNewReward';
import ImageCloseButton from './ImageCloseButton';
import CustomTextInput from '../games/NewGame/custom-inputs/CustomTextInput';
import CustomSelect from '../games/NewGame/custom-inputs/CustomSelect';
import CustomDatePicker from '../games/NewGame/custom-inputs/CustomDatePicker';
import { format } from 'date-fns';

import { GamifyToast } from '../../common/CustomToasts';
import { useNavigate, useParams } from 'react-router';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';
import ConfirmationDialog from '../../components/ConfirmationDialog';

export default function NewReward() {
  const navigate = useNavigate();
  const { rewardId } = useParams();

  const { theme } = useTheme();
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const isEditMode = Boolean(rewardId);

  const newReward = {
    id: '',
    name: '',
    description: '',
    rewardType: 'no_line',
    personLimit: '1',
    limitCount: '1',
    value: '',
    valueType: '',
    remainingCount: '',
    tags: '',
    validFrom: format(new Date(), 'MM/dd/yyyy'),
    validTo: format(
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      'MM/dd/yyyy'
    ), // 30 days from now
    launchDate: '',
    isActive: 1,
    cost: '100',
    costType: '',
    costName: 'points',
    ownerID: global.me.orgID,
    ownerType: global.me.isAdmin ? 'public' : 'circle',
    approverType: 'auto',
    location: '',
    uploadloadingPicture: false,
    uploadPictureTitle: 'Upload Picture / Video',
    imageName: '',
    imageType: '',
    orgID: global.me.orgID,
    useRewardCatalog: false,
    cloneReward: false,
    utid: '',
    minGiftcardAmount: 0,
    maxGiftcardAmount: 0,
    giftcardValue: '',
    giftcardValueFixed: false,
    giftcardDisclaimer: '',
    tangoAccountID: '',
  };
  const [initialValues, setInitialValues] = useState(() => {
    LOG.debug('Initializing component - isEditMode:', isEditMode);
    LOG.debug('Initializing component - rewardId:', rewardId);
    return isEditMode ? {} : newReward;
  });

  const createRewardValidationSchema = yup.object().shape({
    cost: yup
      .number()
      .required()
      .positive()
      .integer()
      .min(0, 'Value must be at least 0')
      .max(1000000, 'Value can be no larger than 1000000')
      .label('Reward Cost'),
    limitCount: yup
      .mixed()
      .required()
      .test('limitCount-validation', 'Invalid limit count', function (value) {
        if (value === '999999') {
          return true; // Allow unlimited
        }
        const numValue = Number(value);
        if (isNaN(numValue) || numValue < 1 || numValue > 100000) {
          return false;
        }
        return true;
      })
      .label('Limit'),
    personLimit: yup
      .mixed()
      .required()
      .test('personLimit-validation', 'Invalid person limit', function (value) {
        if (value === '999999') {
          return true; // Allow unlimited
        }
        const numValue = Number(value);
        if (isNaN(numValue) || numValue < 1 || numValue > 1000) {
          return false;
        }
        return true;
      })
      .label('Limit'),
    name: yup
      .string()
      .label('Name')
      .required()
      .min(5, 'The name is too short.  Please enter at least 5 characters'),
    tags: yup
      .string()
      .label('Tags')
      .min(5, 'The tag name is too short.  Please enter at least 5 characters')
      .max(32, 'The tag name is too long.'),
    validFrom: yup
      .string()
      .required()
      .label('Start date')
      .matches(
        utils.dateRegExp,
        'Please enter a valid date in the form ' + global.appSettings.dateFormat
      ),
    validTo: yup
      .string()
      .required()
      .label('End date')
      .test('validTo-validation', 'Invalid end date', function (value) {
        // Allow "No Expiration" date (01/01/2100)
        if (value === '01/01/2100') {
          return true;
        }

        // Check if it matches the date format
        if (!utils.dateRegExp.test(value)) {
          return false;
        }

        // Check if validTo is greater than or equal to validFrom
        const { validFrom } = this.parent;
        return new Date(value) >= new Date(validFrom);
      }),
    launchDate: yup
      .string()
      .label('Launch Date')
      .matches(
        utils.dateRegExp,
        'Please enter a valid date in the form form ' +
          global.appSettings.dateFormat
      ),
  });

  /// ///////
  //
  // Get reward
  //
  /// ///////

  async function getRewardForScreen(rewardID) {
    LOG.debug('!!!getRewardForScreen: ', rewardID);

    if (rewardID === '' || rewardID === undefined) {
      return initialValues;
    }

    const myQuery = gql`
      query getRewardDetails($rewardID: ID!) {
        getReward(id: $rewardID) {
          id
          isDeleted
          approverType
          cost
          costType
          description
          imageName
          imageType
          isActive
          limitCount
          location
          name
          personLimit
          remainingCount
          rewardType
          validFrom
          validTo
          value
          valueType
          ownerType
          utid
          giftcardDisclaimer
          giftcardValue
          minGiftcardAmount
          maxGiftcardAmount
          tangoAccountID
        }
      }
    `;

    const rewardData = await API.graphql({
      query: myQuery,
      variables: { rewardID },
    });

    LOG.debug('!!!rewardData: ', rewardData);
    if (!rewardData.data.getReward) {
      setInitialValues(newReward);
      return newReward;
    }

    const reward = rewardData.data.getReward;
    const ourReward = {
      id: reward.id,
      name: reward.name,
      description: reward.description,
      rewardType: reward.rewardType,
      personLimit: reward.personLimit.toString(),
      limitCount: reward.limitCount.toString(),
      value: reward.value.toString(),
      valueType: reward.valueType,
      imageName: reward.imageName,
      imageType: reward.imageType,
      tags: reward.tags,
      validFrom: defaultDateFormat(
        blankIfValue(reward.validFrom, utils.MIN_DATE)
      ),
      validTo:
        reward.validTo === '2100-01-01'
          ? '01/01/2100'
          : defaultDateFormat(blankIfValue(reward.validTo, utils.MAX_DATE)),
      launchDate: defaultDateFormat(
        blankIfValue(reward.launchDate, utils.MAX_DATE)
      ),
      isActive: reward.isActive,
      cost: reward.cost.toString(),
      costType: reward.costType,
      ownerID: reward.ownerID,
      approverType: reward.approverType,
      location: reward.location,
      orgID: reward.orgID,
      remainingCount: reward.remainingCount,
      ownerType: reward.ownerType,
      createdBy: global.me.id,
      uploadPictureTitle: 'Upload Picture / Video',
      useRewardCatalog: false,
      cloneReward: false,
      utid: reward.utid,
      minGiftcardAmount: utils.defaultIfEmpty(
        Math.max(Math.round(parseFloat(reward.minGiftcardAmount)), 1),
        1
      ),
      maxGiftcardAmount: utils.defaultIfEmpty(
        Math.max(Math.round(parseFloat(reward.maxGiftcardAmount)), 1),
        1
      ),
      giftcardValue: reward.giftcardValue.toString(),
      giftcardValueFixed: false,
      tangoAccountID:
        reward.tangoAccountID !== 'NA' ? reward.tangoAccountID : '',
    };

    LOG.debug('Setting initial values for edit mode:', ourReward);
    LOG.debug('limitCount:', ourReward.limitCount);
    LOG.debug('personLimit:', ourReward.personLimit);
    LOG.debug('validTo:', ourReward.validTo);
    setInitialValues(ourReward);
  }

  /// ////////
  //
  // Update reward
  //
  /// ////////

  async function updateRewardRecord(reward) {
    LOG.debug('updateRewardRecord: ', reward);

    if (reward.ownerType === 'public') {
      reward.ownerID = global.me.orgID;
    }
    const ourReward = {
      id: reward.id,
      name: reward.name,
      description: reward.description,
      rewardType: reward.rewardType,
      personLimit: reward.personLimit.toString().trim(),
      limitCount: reward.limitCount.toString().trim(),
      value: reward.value.toString().trim(),
      valueType: reward.valueType,
      remainingCount: reward.remainingCount.toString().trim(),
      imageName: reward.imageName,
      imageType: reward.imageType,
      tags: reward.tags,
      validFrom: graphqlDateFormat(reward.validFrom),
      validTo: graphqlDateFormat(reward.validTo),
      launchDate: graphqlDateFormat(
        utils.defaultIfEmpty(reward.launchDate, reward.validFrom)
      ),
      isActive: reward.isActive,
      cost: reward.cost.toString().trim(),
      costType: reward.costType,
      ownerID: reward.ownerID,
      approverType: reward.approverType,
      location: reward.location,
      orgID: reward.orgID,
      ownerType: reward.ownerType,
      createdBy: global.me.id,
      isDeleted: false,
      giftcardValue: Number(utils.defaultIfEmpty(reward.giftcardValue, 0)),
      utid: reward.utid,
      giftcardDisclaimer: reward.giftcardDisclaimer,
      minGiftcardAmount: utils.defaultIfEmpty(
        Math.max(Math.round(parseFloat(reward.minGiftcardAmount)), 1),
        1
      ),
      maxGiftcardAmount: utils.defaultIfEmpty(
        Math.max(Math.round(parseFloat(reward.maxGiftcardAmount)), 1),
        1
      ),
      tangoAccountID:
        reward.tangoAccountID !== '' ? reward.tangoAccountID : 'NA',
    };

    try {
      // reward.isActive = 1;
      // reward.remainingCount = reward.limitCount;

      await API.graphql({
        query: mutations.updateReward,
        variables: { input: ourReward },
      });
      GamifyToast.success(
        'Reward Updated',
        'Your reward has been successfully updated'
      );
      ourReward.validFrom = defaultDateFormat(
        blankIfValue(ourReward.validFrom, utils.MIN_DATE)
      );
      ourReward.validTo = defaultDateFormat(
        blankIfValue(ourReward.validTo, utils.MAX_DATE)
      );
      ourReward.launchDate = defaultDateFormat(
        blankIfValue(ourReward.launchDate, utils.MAX_DATE)
      );
      setInitialValues(ourReward);

      // setFormState(initialState);
      // setButtonState(initialButtonState);
    } catch (err) {
      GamifyToast.error(
        'Update Failed',
        'There was an error updating your reward'
      );
      LOG.error('updateReward(): error updating Reward:' + err);
      return false;
    }
    return true;
  }

  /// ////////
  //
  // Create reward
  //
  /// ////////

  async function createRewardRecord(reward) {
    LOG.debug('createRewardRecord: ', reward);

    if (reward.id !== '') {
      const ret = await updateRewardRecord(reward);
      return ret;
    }

    if (reward.ownerType === 'public') {
      reward.ownerID = global.me.orgID;
      reward.ownerName = 'Everyone';
    }

    const ourReward = {
      name: reward.name,
      description: reward.description,
      rewardType: reward.rewardType.toString().trim(),
      personLimit: reward.personLimit.toString().trim(),
      limitCount: reward.limitCount.toString().trim(),
      value: 0, //      value: reward.value,
      valueType: reward.valueType,
      remainingCount: reward.limitCount.toString().trim(),
      imageName: reward.imageName,
      imageType: reward.imageType,
      tags: reward.tags,
      validFrom: graphqlDateFormat(reward.validFrom),
      validTo: graphqlDateFormat(reward.validTo),
      launchDate: graphqlDateFormat(
        utils.defaultIfEmpty(reward.launchDate, reward.validFrom)
      ),
      isActive: 1, //      isActive: reward.isActive,
      cost: reward.cost.toString().trim(),
      costType: reward.costType,
      ownerID: reward.ownerID,
      approverType: reward.approverType,
      location: reward.location,
      ownerType: reward.ownerType,
      createdBy: global.me.id,
      orgID: global.me.orgID,
      isDeleted: false,
      giftcardValue: Number(utils.defaultIfEmpty(reward.giftcardValue, '0')),
      utid: reward.utid,
      giftcardDisclaimer: reward.giftcardDisclaimer,
      minGiftcardAmount: utils.defaultIfEmpty(
        Math.max(Math.round(parseFloat(reward.minGiftcardAmount)), 1),
        1
      ),
      maxGiftcardAmount: utils.defaultIfEmpty(
        Math.max(Math.round(parseFloat(reward.maxGiftcardAmount)), 1),
        1
      ),
      tangoAccountID:
        reward.tangoAccountID !== '' ? reward.tangoAccountID : 'NA',
    };

    try {
      await API.graphql(graphqlOperation(createReward, { input: ourReward }));
      setInitialValues(newReward);

      GamifyToast.success('Reward Created');
    } catch (err) {
      LOG.error('createReward(): error creating Reward:' + err);
      GamifyToast.error('There was an error creating your reward');
      return false;
    }
    return true;
  }

  async function doSubmit(values, actions) {
    LOG.debug('createReward doSubmit: ', values, actions);

    await new Promise((resolve) => setTimeout(resolve, 500));

    if (values.utid !== '' && values.tangoAccountID === '') {
      GamifyToast.error(
        'Missing Account',
        'You must select an account to pay for a giftcard.'
      );
      actions.setSubmitting(false);
      return;
    }
    const ret = await createRewardRecord(values);
    if (ret) {
      LOG.debug('New Record CREATED');
      window.location.href = '/performance/rewards';
    }
    actions.setSubmitting(false);
  }

  /// //////////////////////////////////////////////////////////////////////////
  //
  // Main screen functionality
  //
  /// //////////////////////////////////////////////////////////////////////////

  useEffect(() => {
    if (rewardId) {
      // Edit mode - fetch the reward data
      LOG.debug('Fetching reward for edit mode: ', rewardId);
      getRewardForScreen(rewardId);
    }
  }, [rewardId]);

  let ownerList = [
    { label: 'Anyone can purchase', value: 'public' },
    {
      label:
        'Only ' +
        global.appSettings.labels.circle.singular +
        ' members can purchase',
      value: 'circle',
    },
    {
      label: 'Attach to a ' + global.appSettings.labels.mission.singular,
      value: 'mission',
    },
  ];

  if (!global.me.isAdmin) {
    ownerList = [
      {
        label:
          'Only ' +
          global.appSettings.labels.circle.singular +
          ' members can purchase',
        value: 'circle',
      },
      {
        label: 'Attach to a ' + global.appSettings.labels.mission.singular,
        value: 'mission',
      },
    ];
  }

  return (
    <React.Fragment>
      <ConfirmationDialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        onConfirm={() => navigate('/performance/rewards')}
        title={`Are you sure you wish to cancel the reward ${
          isEditMode ? 'editing' : 'creation'
        } process?`}
      />
      {/* <img
        width="100%"
        style={{
          position: 'absolute',
          zIndex: -1,
          backgroundColor: theme.background1,
        }}
        src="/images/new-reward-background.svg"
      /> */}
      <Box
        style={{
          fontFamily: 'Manrope',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 700,
          width: 'fit-content',
          marginTop: 20,
          marginBottom: 30,
          marginLeft: 20,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: theme.background2,
        }}
        onClick={() => setOpenCancelDialog(true)}
      >
        <img
          src={`/images/back${theme.isDark ? '-white' : ''}.svg`}
          alt="back"
          width={10}
          height={10}
        />
        <Typography style={{ color: theme.text }}>Cancel</Typography>
      </Box>
      <Box
        style={{
          padding: '20px 100px 40px 100px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <NewRewardTitle>
          {isEditMode ? 'Edit Reward' : 'New Reward'}
        </NewRewardTitle>
        <Formik
          key={JSON.stringify(initialValues)}
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={createRewardValidationSchema}
          onSubmit={(values, actions) => doSubmit(values, actions)}
        >
          {(formikProps) => {
            LOG.debug('Formik values:', formikProps.values);
            LOG.debug('limitCount in form:', formikProps.values.limitCount);
            LOG.debug('personLimit in form:', formikProps.values.personLimit);
            LOG.debug('validTo in form:', formikProps.values.validTo);
            return (
              <Form>
                <Box style={{ width: 500, height: 300 }}>
                  {formikProps.values.imageName !== '' ? (
                    <Box
                      style={{
                        justifyContent: 'space-around',
                        width: 500,
                        border: '1px solid #FAC8B2',
                        height: 300,
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                      }}
                    >
                      <ImageCloseButton />
                      <DisplayMedia
                        imageName={formikProps.values.imageName}
                        imageType={formikProps.values.imageType}
                        objectFit="contain"
                      />
                    </Box>
                  ) : (
                    <UploadMediaNewReward formikProps={formikProps} />
                  )}
                </Box>

                <ViewInputsContainer>
                  <InputTitleContainer
                    style={{ backgroundColor: theme.background1 }}
                  >
                    <InputTitleText>About</InputTitleText>
                    <InputTitleIcon src="/images/about.svg" />
                  </InputTitleContainer>
                  <InputBodyContainer
                    style={{ backgroundColor: theme.background3 }}
                  >
                    <CustomTextInput
                      label="Name"
                      placeholder="E.g. Apple Watch"
                      value={formikProps.values.name}
                      onChange={(newValue) => {
                        formikProps.setFieldValue('name', newValue);
                      }}
                      errorMessage={formikProps.errors.name}
                    />
                    <CustomTextInput
                      label="Description (optional)"
                      placeholder="E.g. Apple watch 2021 in 5 different colors."
                      value={formikProps.values.description}
                      onChange={(newValue) => {
                        formikProps.setFieldValue('description', newValue);
                      }}
                      errorMessage={formikProps.errors.description}
                      type="TextArea"
                    />
                  </InputBodyContainer>
                </ViewInputsContainer>
                <ViewInputsContainer>
                  <InputTitleContainer
                    style={{ backgroundColor: theme.background1 }}
                  >
                    <InputTitleText>Settings</InputTitleText>
                    <InputTitleIcon src="/images/settings.svg" />
                  </InputTitleContainer>
                  <InputBodyContainer
                    style={{ backgroundColor: theme.background3 }}
                  >
                    <CustomTextInput
                      label="Cost"
                      placeholder="E.g. 500"
                      value={formikProps.values.cost}
                      onChange={(newValue) => {
                        formikProps.setFieldValue('cost', newValue);
                      }}
                      errorMessage={formikProps.errors.cost}
                    />
                    <div style={{ marginBottom: 16 }}>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: theme.custom?.text || '#333',
                          marginBottom: 8,
                        }}
                      >
                        How many are Available?
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <input
                          type="text"
                          placeholder="E.g. 10"
                          value={
                            formikProps.values.limitCount === '999999'
                              ? 'Unlimited'
                              : formikProps.values.limitCount
                          }
                          onChange={(e) => {
                            formikProps.setFieldValue(
                              'limitCount',
                              e.target.value
                            );
                          }}
                          disabled={formikProps.values.limitCount === '999999'}
                          style={{
                            flex: 1,
                            padding: '14px 16px',
                            fontSize: 14,
                            fontWeight: 500,
                            border: `1px solid ${
                              formikProps.errors.limitCount
                                ? '#FF5C77'
                                : theme.custom?.stroke || '#e0e0e0'
                            }`,
                            borderRadius: 8,
                            backgroundColor:
                              theme.custom?.background1 || 'white',
                            color:
                              formikProps.values.limitCount === '999999'
                                ? theme.custom?.primaryLight || '#FAC8B2'
                                : theme.custom?.text || '#333',
                            outline: 'none',
                            transition: 'border-color 0.2s ease',
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (formikProps.values.limitCount === '999999') {
                              formikProps.setFieldValue('limitCount', '');
                            } else {
                              formikProps.setFieldValue('limitCount', '999999');
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            border: `1px solid ${
                              formikProps.values.limitCount === '999999'
                                ? 'transparent'
                                : theme.custom?.stroke || '#e0e0e0'
                            }`,
                            borderRadius: 6,
                            backgroundColor:
                              formikProps.values.limitCount === '999999'
                                ? theme.custom?.primary || '#FF6A00'
                                : 'transparent',
                            color:
                              formikProps.values.limitCount === '999999'
                                ? 'white'
                                : theme.custom?.text || '#333',
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          Unlimited
                        </button>
                      </div>
                      {formikProps.errors.limitCount && (
                        <div
                          style={{
                            color: '#FF5C77',
                            fontSize: 14,
                            marginTop: 8,
                            fontWeight: 600,
                          }}
                        >
                          {formikProps.errors.limitCount}
                        </div>
                      )}
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: theme.custom?.text || '#333',
                          marginBottom: 8,
                        }}
                      >
                        How many can each person buy?
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <input
                          type="text"
                          placeholder="E.g. 2"
                          value={
                            formikProps.values.personLimit === '999999'
                              ? 'Unlimited'
                              : formikProps.values.personLimit
                          }
                          onChange={(e) => {
                            formikProps.setFieldValue(
                              'personLimit',
                              e.target.value
                            );
                          }}
                          disabled={formikProps.values.personLimit === '999999'}
                          style={{
                            flex: 1,
                            padding: '14px 16px',
                            fontSize: 14,
                            fontWeight: 500,
                            border: `1px solid ${
                              formikProps.errors.personLimit
                                ? '#FF5C77'
                                : theme.custom?.stroke || '#e0e0e0'
                            }`,
                            borderRadius: 8,
                            backgroundColor:
                              theme.custom?.background1 || 'white',
                            color:
                              formikProps.values.personLimit === '999999'
                                ? theme.custom?.primaryLight || '#FAC8B2'
                                : theme.custom?.text || '#333',
                            outline: 'none',
                            transition: 'border-color 0.2s ease',
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (formikProps.values.personLimit === '999999') {
                              formikProps.setFieldValue('personLimit', '');
                            } else {
                              formikProps.setFieldValue(
                                'personLimit',
                                '999999'
                              );
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            border: `1px solid ${
                              formikProps.values.personLimit === '999999'
                                ? 'transparent'
                                : theme.custom?.stroke || '#e0e0e0'
                            }`,
                            borderRadius: 6,
                            backgroundColor:
                              formikProps.values.personLimit === '999999'
                                ? theme.custom?.primary || '#FF6A00'
                                : 'transparent',
                            color:
                              formikProps.values.personLimit === '999999'
                                ? 'white'
                                : theme.custom?.text || '#333',
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          Unlimited
                        </button>
                      </div>
                      {formikProps.errors.personLimit && (
                        <div
                          style={{
                            color: '#FF5C77',
                            fontSize: 14,
                            marginTop: 8,
                            fontWeight: 600,
                          }}
                        >
                          {formikProps.errors.personLimit}
                        </div>
                      )}
                    </div>
                    <DateContainer>
                      <CustomDatePicker
                        label="Start date"
                        onChange={(newValue) => {
                          formikProps.setFieldValue(
                            'validFrom',
                            format(new Date(newValue), 'MM/dd/yyyy')
                          );
                        }}
                        value={
                          formikProps.values.validFrom
                            ? new Date(
                                formikProps.values.validFrom
                              ).toISOString()
                            : ''
                        }
                        type="Date"
                        placeholder="Select"
                        errorMessage={formikProps.errors.validFrom}
                      />
                      {formikProps.values.validTo === '01/01/2100' ? (
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: '#0B0B0C',
                              marginBottom: 8,
                            }}
                          >
                            End date
                          </div>
                          <input
                            type="text"
                            value="Never"
                            disabled
                            style={{
                              marginTop: 7,
                              width: '100%',
                              height: '56px',
                              padding: '16px',
                              fontSize: 13,
                              fontWeight: 600,
                              border: `1px solid ${
                                theme.custom?.stroke || '#e0e0e0'
                              }`,
                              borderRadius: 8,
                              backgroundColor:
                                theme.custom?.background1 || 'white',
                              color: theme.custom?.textLight || '#666',
                              outline: 'none',
                              fontFamily: 'Manrope',
                              fontFeatureSettings: '"liga" off, "clig" off',
                            }}
                          />
                        </div>
                      ) : (
                        <CustomDatePicker
                          label="End date"
                          onChange={(newValue) => {
                            formikProps.setFieldValue(
                              'validTo',
                              format(new Date(newValue), 'MM/dd/yyyy')
                            );
                          }}
                          value={
                            formikProps.values.validTo
                              ? new Date(
                                  formikProps.values.validTo
                                ).toISOString()
                              : ''
                          }
                          disableBeforeDate={formikProps.values.validFrom}
                          type="Date"
                          placeholder="Select"
                          errorMessage={
                            formikProps.values.validTo === '01/01/2100'
                              ? ''
                              : formikProps.errors.validTo
                          }
                        />
                      )}
                    </DateContainer>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: 16,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          const noExpirationDate =
                            formikProps.values.validTo === '01/01/2100';

                          if (noExpirationDate) {
                            formikProps.setFieldValue(
                              'validTo',
                              format(
                                new Date(
                                  new Date().setDate(new Date().getDate() + 1)
                                ),
                                'MM/dd/yyyy'
                              )
                            );
                          } else {
                            formikProps.setFieldValue('validTo', '01/01/2100');
                          }
                        }}
                        style={{
                          padding: '8px 16px',
                          border: `1px solid ${
                            formikProps.values.validTo === '01/01/2100'
                              ? 'transparent'
                              : theme.custom?.stroke || '#e0e0e0'
                          }`,
                          borderRadius: 6,
                          backgroundColor:
                            formikProps.values.validTo === '01/01/2100'
                              ? theme.custom?.primary || '#FF6A00'
                              : 'transparent',
                          color:
                            formikProps.values.validTo === '01/01/2100'
                              ? 'white'
                              : theme.custom?.text || '#333',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        No Expiration
                      </button>
                    </div>
                  </InputBodyContainer>
                </ViewInputsContainer>
                <ViewInputsContainer>
                  <InputTitleContainer
                    style={{ backgroundColor: theme.background1 }}
                  >
                    <InputTitleText>Permissions</InputTitleText>
                    <InputTitleIcon src="/images/permissions.svg" />
                  </InputTitleContainer>
                  <InputBodyContainer
                    style={{ backgroundColor: theme.background3 }}
                  >
                    <CustomSelect
                      label="Who can purchase?"
                      value={formikProps.values.ownerType}
                      onChange={(newValue) => {
                        formikProps.setFieldValue('ownerType', newValue);
                      }}
                      items={ownerList}
                    />
                    <CustomSelect
                      label="Who approves reward purchases?"
                      value={formikProps.values.approverType}
                      onChange={(newValue) => {
                        formikProps.setFieldValue('approverType', newValue);
                      }}
                      items={[
                        {
                          label: 'No approval required',
                          value: 'auto',
                        },
                        {
                          label:
                            global.appSettings.labels.reward.proper + ' owner',
                          value: 'owner',
                        },
                        {
                          label:
                            global.appSettings.labels.circle.proper + ' owner',
                          value: 'circle_owner',
                        },
                        {
                          label:
                            global.appSettings.labels.circle.proper +
                            ' moderator',
                          value: 'moderator',
                        },
                      ]}
                    />
                  </InputBodyContainer>
                </ViewInputsContainer>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: 20,
                  }}
                >
                  <button
                    disabled={!formikProps.isValid || formikProps.isSubmitting}
                    type={'submit'}
                    style={{
                      backgroundColor:
                        formikProps.isValid && !formikProps.isSubmitting
                          ? '#FF6A00'
                          : '#737373',
                      padding: 12,
                      width: '50%',
                      border: 0,
                      borderRadius: 10,
                      color: 'white',
                      fontWeight: 700,
                      cursor: formikProps.isSubmitting
                        ? 'not-allowed'
                        : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                    }}
                  >
                    {formikProps.isSubmitting && (
                      <CircularProgress size={16} color="inherit" />
                    )}
                    {formikProps.isSubmitting
                      ? 'Saving...'
                      : isEditMode
                      ? 'Update reward'
                      : 'Save changes'}
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </React.Fragment>
  );
}
